import React, { useRef } from 'react'
import { useFulfillment } from '../FulfillmentProvider/FulfillmentProvider'
import {
  getArgsForSubmit,
  getInitialValues,
  getValidationSchema,
  deriveCheckoutStateFromUserInput,
  CheckoutFormValues
} from './utils'
import { useCart } from '@local/do-secundo-cart-provider/src'
import { Form } from 'formik'
import { LoadingOverlay } from '@local/do-secundo-loading'
import { DomesticCheckoutFormBody } from './DomesticCheckoutFormBody'
import { InternationalCheckoutFormBody } from './InternationalCheckoutFormBody'
import { useCheckoutProps } from './useCheckoutProps'
import * as CustomerInfo from './CustomerInfo/CustomerInfo'
import { Loading, LoadingVariant } from '@local/do-secundo-loading'
import { ErrorComponent } from '@local/do-secundo-error'
import { Formik } from 'formik'
import { getCheckAmountExcludingTax } from '../../utils/cart-helpers'
import { useHandlePlaceOrder } from '../CheckoutPage/utils'
import { useSentry } from 'banquet-runtime-modules'
import { useCustomerAuth } from '../../auth/CustomerAuthContext'
import { useCurrency } from '@local/do-secundo-use-currency'
import styles from './CheckoutForm.module.css'

interface Props {
  setEditingAccount: (editing: boolean) => void
  setIsAuthModalOpen: (value: boolean) => void
  isAuthModalOpen: boolean
}

/**
 * The root checkout component that manages authentication, form initialization, and submission.
 * Wraps CheckoutFormContent with Formik and handles the login prompt for unauthenticated users.
 * Determines initial payment type and validation rules based on restaurant configuration.
 */
export const CheckoutForm: React.FC<Props> = ({
  setEditingAccount,
  setIsAuthModalOpen,
  isAuthModalOpen
}) => {
  const { captureException } = useSentry()
  const { currencyFormatter } = useCurrency()
  const fulfillmentContext = useFulfillment()
  const { willEarnLoyalty, setWillEarnLoyalty } = useCart()
  const currentFormValuesRef = useRef<Partial<CheckoutFormValues> | undefined>(
    undefined
  )

  // Only get authedCustomer for initial form values
  // All other auth logic is handled in DomesticCheckoutFormBody
  const { customer: authedCustomer } = useCustomerAuth()

  const { handlePlaceOrder, confirmOrderError } = useHandlePlaceOrder()

  const checkoutProps = useCheckoutProps()

  const {
    cart,
    cartValid,
    isLoading,
    isError,
    isIntlRx,
    eventType,
    tdsConfig,
    marketingConfig,
    initialPaymentType,
    paymentValidationMethod,
    isPayLaterRequired,
    country,
    checkoutPaymentAmount
  } = checkoutProps

  if (fulfillmentContext.loading || isLoading) {
    return <Loading variant={LoadingVariant.SECONDARY} />
  }

  if (fulfillmentContext.error || isError) {
    return (
      <ErrorComponent error={fulfillmentContext.error} isActionable={false} />
    )
  }

  const initialValues = getInitialValues({
    fulfillmentContext,
    formFields: eventType?.formFields || [],
    authedCustomer,
    customer: cart?.order.checks[0].customer,
    paymentType: initialPaymentType,
    preComputedTips:
      cart?.allowTipping && initialPaymentType !== 'OTHER'
        ? cart?.preComputedTips
        : undefined,
    companyName: cart?.metadata?.companyName || '',
    smsMarkingetingEnabled: marketingConfig?.smsMarketingEnabled,
    isIntlRx,
    currentValues: currentFormValuesRef.current,
    restaurantCountry: country
  })

  const validationSchema = getValidationSchema({
    fulfillmentContext,
    formFields: eventType?.formFields || [],
    paymentValidationMethod,
    tdsConfig: tdsConfig ?? { enabled: false },
    cartAmount: getCheckAmountExcludingTax(cart),
    currencyFormatter
  })

  const onSubmit = async (values, { setSubmitting }) => {
    try {
      const submitArgs = getArgsForSubmit({
        fulfillmentContext,
        values,
        eventType: eventType!!,
        totalAmountDue: checkoutPaymentAmount.totalAmountDue
      })

      // since we enroll loyalty asynchronously, we will use this to show
      // the confirmation before applied loyalty appears on the order
      setWillEarnLoyalty(willEarnLoyalty || Boolean(submitArgs.loyaltyEnroll))

      await handlePlaceOrder(submitArgs)
    } catch (e) {
      console.warn('onSubmit', e)
      const logError =
        e instanceof Error
          ? e
          : new Error(`Error placing order ${JSON.stringify(e)}`)
      captureException(logError)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      enableReinitialize
      data-testid='checkout-form'
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange
      initialTouched={{ paymentTip: true }} //shows the tip error on page mount if one exists
      onSubmit={onSubmit}
    >
      {({ values, isSubmitting, isValid, handleSubmit, setFieldValue }) => {
        // Store current form values for preservation during reinitialization
        currentFormValuesRef.current = values

        const customer = CustomerInfo.getArgsForSubmit({ values }).customer

        // Calculate state derived from user's form input
        const userInputDerivedState = deriveCheckoutStateFromUserInput({
          values,
          cart,
          isPayLaterRequired
        })

        // Props for both checkout form bodies
        const checkoutFormProps = {
          // Spread user input derived state first to allow explicit overrides
          ...userInputDerivedState,
          customer,
          values,
          isSubmitting,
          canCheckout: isValid && !fulfillmentContext.updating && cartValid,
          confirmOrderError,
          setEditingAccount,
          setIsAuthModalOpen,
          isAuthModalOpen,
          checkoutProps,
          handleSubmit,
          setFieldValue
        }

        return (
          <Form className={styles.form} onSubmit={handleSubmit}>
            <LoadingOverlay isVisible={isSubmitting} />
            {isIntlRx ? (
              <InternationalCheckoutFormBody {...checkoutFormProps} />
            ) : (
              <DomesticCheckoutFormBody {...checkoutFormProps} />
            )}
          </Form>
        )
      }}
    </Formik>
  )
}
